import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { parseLocalDate } from '../utils/dates';
import { useFacility } from '../contexts/FacilityContext';
import FacilityFilter from '../components/FacilityFilter';
import ActiveFacilityBanner from '../components/ActiveFacilityBanner';
import { fetchPatients, createPatient, restorePatient } from '../services/api';
import DeletePatientModal from '../components/DeletePatientModal';
import './Patients.css';

function Patients() {
  const navigate = useNavigate();
  const { selectedFacility, facilities } = useFacility();
  const [manualFilter, setManualFilter] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Active vs. archived (soft-deleted) view + row actions
  const [view, setView] = useState('active'); // 'active' | 'archived'
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [restoringId, setRestoringId] = useState(null);
  const [actionError, setActionError] = useState(null);

  // Create patient modal state
  const [createModal, setCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [createForm, setCreateForm] = useState({
    firstName: '', lastName: '', dob: '', sex: 'U',
    phone: '', email: '', streetAddress: '', city: '', state: '', zip: '', mrn: '',
  });

  const createFormValid =
    createForm.firstName.trim() && createForm.lastName.trim() && createForm.dob;

  const openCreateModal = () => {
    setCreateForm({ firstName: '', lastName: '', dob: '', sex: 'U', phone: '', email: '', streetAddress: '', city: '', state: '', zip: '', mrn: '' });
    setCreateError(null);
    setCreateModal(true);
  };

  const handleCreatePatient = async () => {
    setCreateError(null);
    setCreating(true);
    try {
      await createPatient({
        facility_id:    selectedFacility.id,
        first_name:     createForm.firstName,
        last_name:      createForm.lastName,
        date_of_birth:  createForm.dob,
        sex:            createForm.sex,
        phone:          createForm.phone          || null,
        email:          createForm.email          || null,
        street_address: createForm.streetAddress  || '',
        city:           createForm.city           || '',
        state:          createForm.state          || '',
        zip:            createForm.zip            || '',
        mrn:            createForm.mrn            || null,
      });
      setCreateModal(false);
      // Refresh the patient list
      const res = await fetchPatients(selectedFacility.id);
      setPatients(res.data || []);
    } catch (err) {
      setCreateError(err.response?.data?.error || 'Failed to create patient');
    } finally {
      setCreating(false);
    }
  };

  // Build a quick id→name lookup from the live facilities list
  const facilityNameMap = useMemo(
    () => Object.fromEntries(facilities.map((f) => [f.id, f.name])),
    [facilities]
  );

  // Fetch patients whenever the dashboard-selected facility, the active/archived
  // view, or a manual refresh (after delete/restore) changes.
  // When a facility is selected, the API filters server-side.
  // When none is selected, we fetch all and let manualFilter handle client-side narrowing.
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const params = {};
    if (selectedFacility) params.facility_id = selectedFacility.id;
    if (view === 'archived') params.archived = 1;

    fetchPatients(Object.keys(params).length ? params : null)
      .then((res) => {
        if (!cancelled) {
          setPatients(res.data || []);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.data?.error || 'Failed to load patients');
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [selectedFacility, view, refreshKey]);

  const handleRestore = async (patient) => {
    setRestoringId(patient.id);
    setActionError(null);
    try {
      await restorePatient(patient.id);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setActionError(err?.response?.data?.error || 'Failed to restore patient.');
    } finally {
      setRestoringId(null);
    }
  };

  const filteredPatients = useMemo(() => {
    let list = patients;

    // Manual facility filter only applies when no dashboard facility is active
    if (!selectedFacility && manualFilter.length > 0) {
      list = list.filter((p) => manualFilter.includes(p.facility_id));
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (p) =>
          `${p.first_name} ${p.last_name}`.toLowerCase().includes(q) ||
          (p.email || '').toLowerCase().includes(q) ||
          (p.phone || '').includes(q) ||
          (p.insurance_provider || '').toLowerCase().includes(q)
      );
    }

    return list;
  }, [patients, manualFilter, selectedFacility, searchQuery]);

  const subtitle = view === 'archived'
    ? (selectedFacility ? `Archived patients at ${selectedFacility.name}` : 'Archived (deleted) patients across all facilities')
    : (selectedFacility ? `Patients at ${selectedFacility.name}` : 'Master patient list across all facilities');

  return (
    <div className="patients-page">
      <ActiveFacilityBanner />
      <div className="patients-header">
        <div>
          <h1 className="patients-title">Patients</h1>
          <p className="patients-subtitle">{subtitle}</p>
        </div>
        <div className="patients-header-right">
          {!selectedFacility && (
            <FacilityFilter
              selectedIds={manualFilter}
              onChange={setManualFilter}
            />
          )}
          <div className="pt-add-patient-wrapper">
            <button
              className="pt-btn-add-patient"
              onClick={openCreateModal}
              disabled={!selectedFacility}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Add Patient
            </button>
            {!selectedFacility && (
              <p className="pt-add-patient-hint">Select a facility to add patients</p>
            )}
          </div>
        </div>
      </div>

      {/* Search + Count */}
      <div className="patients-toolbar">
        <div className="patients-search-wrapper">
          <svg className="patients-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            className="patients-search"
            placeholder="Search by name, email, phone, or insurance..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="patients-search-clear" onClick={() => setSearchQuery('')}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
        <div className="patients-view-toggle">
          <button
            className={`patients-view-btn ${view === 'active' ? 'active' : ''}`}
            onClick={() => { setView('active'); setActionError(null); }}
          >
            Active
          </button>
          <button
            className={`patients-view-btn ${view === 'archived' ? 'active' : ''}`}
            onClick={() => { setView('archived'); setActionError(null); }}
          >
            Archived
          </button>
        </div>
        {!loading && (
          <span className="patients-count">
            {filteredPatients.length} {filteredPatients.length === 1 ? 'patient' : 'patients'}
          </span>
        )}
      </div>

      {actionError && (
        <div className="patients-action-error">{actionError}</div>
      )}

      {/* Table */}
      <div className="patients-table-card">
        {loading ? (
          <div className="patients-empty">
            <p>Loading patients…</p>
          </div>
        ) : error ? (
          <div className="patients-empty">
            <p style={{ color: '#ef4444' }}>{error}</p>
          </div>
        ) : filteredPatients.length === 0 ? (
          <div className="patients-empty">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
            </svg>
            <h3>{view === 'archived' ? 'No archived patients' : 'No patients found'}</h3>
            <p>{view === 'archived' ? 'Deleted patients will appear here' : 'Try adjusting your search or facility filter'}</p>
          </div>
        ) : (
          <table className="patients-table">
            <thead>
              <tr>
                <th>Patient Name</th>
                <th>Date of Birth</th>
                <th>Email</th>
                <th>Phone</th>
                {view === 'archived' ? <th>Reason</th> : <th>Insurance</th>}
                <th>Facility</th>
                <th className="patients-actions-th">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPatients.map((patient) => (
                <tr key={patient.id} onClick={() => navigate(`/patients/${patient.id}`)} style={{ cursor: 'pointer' }}>
                  <td className="patient-name-cell">
                    <div className="patient-avatar">
                      {patient.first_name[0]}{patient.last_name[0]}
                    </div>
                    <span>{patient.first_name} {patient.last_name}</span>
                  </td>
                  <td>{parseLocalDate(patient.date_of_birth).toLocaleDateString()}</td>
                  <td className="patient-email">{patient.email || '—'}</td>
                  <td>{patient.phone}</td>
                  {view === 'archived' ? (
                    <td className="patient-archive-reason" title={patient.archive_reason || ''}>
                      {patient.archive_reason || '—'}
                    </td>
                  ) : (
                    <td>
                      {patient.insurance_provider
                        ? <span className="insurance-badge">{patient.insurance_provider}</span>
                        : '—'}
                    </td>
                  )}
                  <td className="facility-cell">{facilityNameMap[patient.facility_id] || '—'}</td>
                  <td className="patients-actions-cell" onClick={(e) => e.stopPropagation()}>
                    {view === 'archived' ? (
                      <button
                        className="patients-btn-restore"
                        onClick={() => handleRestore(patient)}
                        disabled={restoringId === patient.id}
                      >
                        {restoringId === patient.id ? 'Restoring…' : 'Restore'}
                      </button>
                    ) : (
                      <button
                        className="patients-btn-delete"
                        onClick={() => { setActionError(null); setDeleteTarget(patient); }}
                        title="Delete patient"
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {/* Create Patient Modal */}
      {createModal && (
        <div className="pt-modal-backdrop">
          <div className="pt-modal">
            {/* Header */}
            <div className="pt-modal-header">
              <div className="pt-modal-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <line x1="19" y1="8" x2="19" y2="14" />
                  <line x1="22" y1="11" x2="16" y2="11" />
                </svg>
              </div>
              <div className="pt-modal-header-text">
                <h2 className="pt-modal-title">Add Patient</h2>
                <p className="pt-modal-subtitle">Create a new patient record at {selectedFacility?.name}</p>
              </div>
              <button className="pt-modal-close" onClick={() => { if (!creating) setCreateModal(false); }} disabled={creating}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {/* Form */}
            <div className="pt-modal-section">
              <p className="pt-modal-section-title">Patient Information</p>
              <div className="pt-form-grid">
                <div className="pt-form-field">
                  <label className="pt-form-label">First Name <span className="pt-required">*</span></label>
                  <input className="pt-form-input" type="text" placeholder="First name" value={createForm.firstName} onChange={(e) => setCreateForm((f) => ({...f, firstName: e.target.value}))} disabled={creating} />
                </div>
                <div className="pt-form-field">
                  <label className="pt-form-label">Last Name <span className="pt-required">*</span></label>
                  <input className="pt-form-input" type="text" placeholder="Last name" value={createForm.lastName} onChange={(e) => setCreateForm((f) => ({...f, lastName: e.target.value}))} disabled={creating} />
                </div>
                <div className="pt-form-field">
                  <label className="pt-form-label">Date of Birth <span className="pt-required">*</span></label>
                  <input className="pt-form-input" type="date" value={createForm.dob} onChange={(e) => setCreateForm((f) => ({...f, dob: e.target.value}))} disabled={creating} />
                </div>
                <div className="pt-form-field">
                  <label className="pt-form-label">Sex</label>
                  <select className="pt-form-select" value={createForm.sex} onChange={(e) => setCreateForm((f) => ({...f, sex: e.target.value}))} disabled={creating}>
                    <option value="U">Unknown</option>
                    <option value="M">Male</option>
                    <option value="F">Female</option>
                  </select>
                </div>
                <div className="pt-form-field">
                  <label className="pt-form-label">Phone</label>
                  <input className="pt-form-input" type="tel" placeholder="Phone number" value={createForm.phone} onChange={(e) => setCreateForm((f) => ({...f, phone: e.target.value}))} disabled={creating} />
                </div>
                <div className="pt-form-field">
                  <label className="pt-form-label">Email</label>
                  <input className="pt-form-input" type="email" placeholder="Email address" value={createForm.email} onChange={(e) => setCreateForm((f) => ({...f, email: e.target.value}))} disabled={creating} />
                </div>
                <div className="pt-form-field pt-form-field-full">
                  <label className="pt-form-label">Street Address</label>
                  <input className="pt-form-input" type="text" placeholder="Street address" value={createForm.streetAddress} onChange={(e) => setCreateForm((f) => ({...f, streetAddress: e.target.value}))} disabled={creating} />
                </div>
                <div className="pt-form-field">
                  <label className="pt-form-label">City</label>
                  <input className="pt-form-input" type="text" placeholder="City" value={createForm.city} onChange={(e) => setCreateForm((f) => ({...f, city: e.target.value}))} disabled={creating} />
                </div>
                <div className="pt-form-field">
                  <label className="pt-form-label">State</label>
                  <input className="pt-form-input" type="text" placeholder="ST" maxLength={2} value={createForm.state} onChange={(e) => setCreateForm((f) => ({...f, state: e.target.value.toUpperCase()}))} disabled={creating} />
                </div>
                <div className="pt-form-field">
                  <label className="pt-form-label">ZIP</label>
                  <input className="pt-form-input" type="text" placeholder="ZIP code" value={createForm.zip} onChange={(e) => setCreateForm((f) => ({...f, zip: e.target.value}))} disabled={creating} />
                </div>
                <div className="pt-form-field pt-form-field-full">
                  <label className="pt-form-label">MRN <span className="pt-form-hint">(Medical Record Number at {selectedFacility?.name})</span></label>
                  <input className="pt-form-input" type="text" placeholder="Facility medical record number" value={createForm.mrn} onChange={(e) => setCreateForm((f) => ({...f, mrn: e.target.value}))} disabled={creating} />
                </div>
              </div>
            </div>

            {createError && <div className="pt-modal-error">{createError}</div>}

            <div className="pt-modal-actions">
              <button className="pt-btn-cancel" onClick={() => setCreateModal(false)} disabled={creating}>
                Cancel
              </button>
              <button className="pt-btn-confirm" onClick={handleCreatePatient} disabled={creating || !createFormValid}>
                {creating ? (
                  <><span className="pt-spinner" />Adding…</>
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    Add Patient
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete (archive) Patient Modal */}
      {deleteTarget && (
        <DeletePatientModal
          patientId={deleteTarget.id}
          patientName={`${deleteTarget.first_name} ${deleteTarget.last_name}`}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => {
            setDeleteTarget(null);
            setRefreshKey((k) => k + 1);
          }}
        />
      )}
    </div>
  );
}

export default Patients;
